import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { MiniTimelineStore } from './MiniTimelineStore';
import { MiniAudioPlayer } from './MiniAudioPlayer';

interface MiniTimelineControlsProps {
  timelineStore: MiniTimelineStore;
  audioPlayer: MiniAudioPlayer;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
}

/**
 * Controls for the mini timeline (playback, zoom, position)
 */
export const MiniTimelineControls: React.FC<MiniTimelineControlsProps> = observer(({
  timelineStore,
  audioPlayer,
  onPlayPause,
  onSeek
}) => {
  // Local state
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [playbackRate, setPlaybackRate] = useState("1");
  // Format time as MM:SS.mmm
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }, []);
  
  // Handle zoom controls
  const handleZoomIn = useCallback(() => {
    timelineStore.zoomIn();
  }, [timelineStore]);
  
  const handleZoomOut = useCallback(() => {
    timelineStore.zoomOut();
  }, [timelineStore]);
  
  const handleFitToSelection = useCallback(() => {
    timelineStore.fitToSelection();
  }, [timelineStore]);
  
  const handleFitToTimeline = useCallback(() => {
    timelineStore.fitToTimeline();
  }, [timelineStore]);
  
  // Handle skip backward/forward
  const handleSkipBackward = useCallback(() => {
    const newTime = Math.max(
      timelineStore.totalTimeRange[0],
      timelineStore.currentTime - 1
    );
    onSeek(newTime);
  }, [timelineStore, onSeek]);
  
  const handleSkipForward = useCallback(() => {
    const newTime = Math.min(
      timelineStore.totalTimeRange[1],
      timelineStore.currentTime + 1
    );
    onSeek(newTime);
  }, [timelineStore, onSeek]);
  
  // Toggle loop mode
  const handleToggleLoop = useCallback(() => {
    const newLoopState = !loopEnabled;
    setLoopEnabled(newLoopState);
    
    // Find the visible range or selected elements range for looping
    if (newLoopState) {
      // Use selection range if available, otherwise use visible range
      const selectedElements = timelineStore.elements.filter(e => e.isEditable);
      let startTime, endTime;
      
      if (selectedElements.length > 0) {
        // Find min start time and max end time from selected elements
        startTime = Math.min(...selectedElements
          .filter(e => e.element.type === 'text' && e.adjustedStartTime !== undefined)
          .map(e => e.adjustedStartTime || e.originalStartTime || 0));
        
        endTime = Math.max(...selectedElements
          .filter(e => e.element.type === 'text' && e.adjustedEndTime !== undefined)
          .map(e => e.adjustedEndTime || e.originalEndTime || 0));
      } else {
        // Use visible range if no selection
        [startTime, endTime] = timelineStore.visibleTimeRange;
      }
      
      // Set loop range
      audioPlayer.setLoopRange(startTime, endTime, true);
    } else {
      // Disable looping
      audioPlayer.setLoopRange(0, 0, false);
    }
  }, [loopEnabled, timelineStore.elements, timelineStore.visibleTimeRange, audioPlayer]);
  
  // Handle playback rate
  const handlePlaybackRateChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRate = parseFloat(e.target.value);
    setPlaybackRate(e.target.value);
    audioPlayer.playbackRate = newRate;
  }, [audioPlayer]);
  
  return (
    <ControlsContainer>
      <PlaybackControls>
        <ControlButton onClick={handleSkipBackward} title="Skip backward">
          <SkipBackwardIcon />
        </ControlButton>
        
        <PlayPauseButton onClick={onPlayPause} isPlaying={timelineStore.isPlaying}>
          {timelineStore.isPlaying ? <PauseIcon /> : <PlayIcon />}
        </PlayPauseButton>
        
        <ControlButton onClick={handleSkipForward} title="Skip forward">
          <SkipForwardIcon />
        </ControlButton>

        <LoopButton 
          onClick={handleToggleLoop} 
          title={loopEnabled ? "Disable looping" : "Enable looping"}
          isActive={loopEnabled}
        >
          <LoopIcon />
        </LoopButton>
        
        <SpeedSelector 
          value={playbackRate}
          onChange={handlePlaybackRateChange}
          title="Playback speed"
        >
          <option value="0.25">0.25x</option>
          <option value="0.5">0.5x</option>
          <option value="0.75">0.75x</option>
          <option value="0.9">0.9x</option>
          <option value="1">1.0x</option>
          <option value="1.1">1.1x</option>
          <option value="1.25">1.25x</option>
          <option value="1.5">1.5x</option>
          <option value="2">2.0x</option>
        </SpeedSelector>
      </PlaybackControls>
      
      <TimeDisplay>
        <TimeCode>{formatTime(timelineStore.currentTime)}</TimeCode>
        <TimeRange>
          {formatTime(timelineStore.visibleTimeRange[0])} - {formatTime(timelineStore.visibleTimeRange[1])}
        </TimeRange>
      </TimeDisplay>
      
      <ZoomControls>
        <ControlButton onClick={handleZoomIn} title="Zoom in">
          <ZoomInIcon />
        </ControlButton>
        
        <ControlButton onClick={handleZoomOut} title="Zoom out">
          <ZoomOutIcon />
        </ControlButton>
        
        <ControlButton onClick={handleFitToSelection} title="Fit to selection">
          <FitSelectionIcon />
        </ControlButton>
        
        <ControlButton onClick={handleFitToTimeline} title="Fit to timeline">
          <FitTimelineIcon />
        </ControlButton>
        
        <ZoomLevel>
          {Math.round(timelineStore.zoomLevel * 100)}%
        </ZoomLevel>
      </ZoomControls>
    </ControlsContainer>
  );
});

// Styled components
const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 5px;
  background-color: #222233;
  border-radius: 4px;
  margin-bottom: 10px;
`;

const PlaybackControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimeDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimeCode = styled.div`
  font-family: monospace;
  font-size: 16px;
  font-weight: bold;
  color: #f2d093;
`;

const TimeRange = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: #a9a9a9;
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ZoomLevel = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: #a9a9a9;
  min-width: 50px;
  text-align: center;
`;

const ControlButton = styled.button`
  background-color: #2a2a3c;
  color: #f3e9d7;
  border: none;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: #3a3a4c;
  }
  
  &:active {
    background-color: #4a4a5c;
  }
`;

const PlayPauseButton = styled(ControlButton)<{ isPlaying: boolean }>`
  background-color: ${props => props.isPlaying ? '#6c9ba5' : '#6c9ba5'};
  width: 40px;
  height: 40px;
  
  &:hover {
    background-color: ${props => props.isPlaying ? '#5c8b95' : '#5c8b95'};
  }
`;

const LoopButton = styled(ControlButton)<{ isActive: boolean }>`
  background-color: ${props => props.isActive ? '#a46c85' : '#2a2a3c'};
  color: ${props => props.isActive ? '#ffffff' : '#f3e9d7'};
  
  &:hover {
    background-color: ${props => props.isActive ? '#b57d96' : '#3a3a4c'};
  }
`;

const SpeedSelector = styled.select`
  background-color: #2a2a3c;
  color: #f3e9d7;
  border: none;
  border-radius: 4px;
  padding: 5px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background-color: #3a3a4c;
  }
`;

// SVG Icons components
const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 2L13 8L3 14V2Z" fill="currentColor" />
  </svg>
);

const PauseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="2" width="4" height="12" fill="currentColor" />
    <rect x="9" y="2" width="4" height="12" fill="currentColor" />
  </svg>
);

const SkipBackwardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3L5 8L12 13V3Z" fill="currentColor" />
    <rect x="3" y="3" width="2" height="10" fill="currentColor" />
  </svg>
);

const SkipForwardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 3L11 8L4 13V3Z" fill="currentColor" />
    <rect x="11" y="3" width="2" height="10" fill="currentColor" />
  </svg>
);

const ZoomInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    <line x1="10" y1="10" x2="13" y2="13" stroke="currentColor" strokeWidth="2" />
    <line x1="5" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="2" />
    <line x1="7" y1="5" x2="7" y2="9" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ZoomOutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    <line x1="10" y1="10" x2="13" y2="13" stroke="currentColor" strokeWidth="2" />
    <line x1="5" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const FitSelectionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="12" height="10" stroke="currentColor" strokeWidth="1" />
    <rect x="4" y="5" width="8" height="6" fill="currentColor" fillOpacity="0.4" />
    <path d="M1 5L3 3L1 1" stroke="currentColor" strokeWidth="1" />
    <path d="M15 5L13 3L15 1" stroke="currentColor" strokeWidth="1" />
    <path d="M1 11L3 13L1 15" stroke="currentColor" strokeWidth="1" />
    <path d="M15 11L13 13L15 15" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const FitTimelineIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="12" height="10" stroke="currentColor" strokeWidth="1" />
    <line x1="5" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1" />
    <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="1" />
    <line x1="5" y1="10" x2="9" y2="10" stroke="currentColor" strokeWidth="1" />
    <path d="M1 5L3 3L1 1" stroke="currentColor" strokeWidth="1" />
    <path d="M15 5L13 3L15 1" stroke="currentColor" strokeWidth="1" />
    <path d="M1 11L3 13L1 15" stroke="currentColor" strokeWidth="1" />
    <path d="M15 11L13 13L15 15" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const LoopIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5 4.5L14 6L12.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.5 11.5L2 10L3.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 6C14 10.4183 10.4183 14 6 14C4.93913 14 3.93771 13.7793 3.04088 13.391" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 10C2 5.58172 5.58172 2 10 2C11.0609 2 12.0623 2.22067 12.9591 2.60896" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);